<template>
  <div class="settings-container">
    <substrate-mount flat>
      <payment-grade-settings
        :rating="payloadData.rating"
        @click="handleChangeSenderField('rating')"
        @submit="handleChangeGradeSettings"
      />

      <toggle-panel
        v-model="payloadData.name.selected"
        :title="$t(payloadData.name.text)"
        @click="handleChangeSenderField('name')"
      >
        <user-icon slot="icon" />
      </toggle-panel>

      <toggle-panel
        v-model="payloadData.email.selected"
        :title="$t(payloadData.email.text)"
        @click="handleChangeEmailSender"
      >
        <mail-icon slot="icon" />
      </toggle-panel>

      <toggle-panel
        v-model="payloadData.phone.selected"
        :title="$t(payloadData.phone.text)"
        @click="handleChangeSenderField('phone')"
      >
        <call-phone-icon slot="icon" />
      </toggle-panel>

      <toggle-panel
        v-model="hasSelectedItem"
        :title="$t(payloadData[selectedItem].text)"
        @click="handleChangeSenderField(selectedItem)"
      >
        <pin-icon v-if="selectedItem === 'address'" slot="icon" />
        <comment-text-icon v-else slot="icon" />

        <div
          slot="title"
          class="popup-selectable-container"
          @click.stop
        >
          <div
            class="selectable-container"
            @click="handleClickToSelectableOption"
          >
            <div class="selectable-title">
              {{ $t(payloadData[selectedItem].text) }}
            </div>
            <div class="selectable-icon">
              <chevron-bottom-icon fill="white"/>
            </div>
          </div>

          <capusta-popup v-if="isDesktop" ref="capustaPopup">
            <ul class="popup_list">
              <li
                v-for="selectValue in selectList"
                :key="selectValue.text"
                class="popup_list-item"
                @click.stop="handleSelectOption(selectValue)"
              >
                {{ $t(selectValue.text) }}
              </li>
            </ul>
          </capusta-popup>

          <cs-dialog v-else v-model="isShownSelectableOptions">
            <dialog-list-layout>
              <option-panel
                v-for="option in selectList"
                :key="option.key"
                :option="option"
                @click="handleSelectOption"
              />
            </dialog-list-layout>
          </cs-dialog>
        </div>
      </toggle-panel>

      <promo-code-settings />
    </substrate-mount>

    <div class="submit-button-container">
      <cs-button
        width="100%"
        type="secondary"
        @click="handlePreview"
      >
        {{ $t('common.preview') }}
      </cs-button>
    </div>

    <payment-auto-redirection-confirm
      @confirm="handleChangeSenderField('email')"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import HTTPService from '@/services/Http';
import logrocket from '@/services/logrocket';

import UserIcon from 'atoms/icons/basic/User';
import MailIcon from 'atoms/icons/basic/Mail';
import CallPhoneIcon from 'atoms/icons/call/CallPhone';
import CommentTextIcon from 'atoms/icons/chatting/CommentText';
import PinIcon from 'atoms/icons/maps/Pin';
import ChevronBottomIcon from 'atoms/icons/arrows/ChevronBottom';

import TogglePanel from 'molecules/panels/TogglePanel';
import SubstrateMount from '@/components/capusta/SubstrateMount';

import OptionPanel from '@/components/capusta/OptionPanel';
import DialogListLayout from 'modules/Dialogs/ListLayout';

import PromoCodeSettings from 'organisms/payment/settings/components/PromoCode';
import PaymentGradeSettings from 'organisms/payment/settings/components/PaymentGrade';
import PaymentAutoRedirectionConfirm from 'organisms/payment/settings/components/PaymentAutoRedirectionConfirm';

import BillForm from '@/pages/invoicing/classes/BillForm';

import { EVENT_BUS } from '@/eventBus';

const SELECTABLE_ITEMS = ['comment', 'message', 'address'];

export default {
  name: 'PaymentFormSettings',
  components: {
    UserIcon,
    MailIcon,
    CallPhoneIcon,
    CommentTextIcon,
    PinIcon,
    ChevronBottomIcon,
    TogglePanel,
    SubstrateMount,
    OptionPanel,
    DialogListLayout,
    PromoCodeSettings,
    PaymentGradeSettings,
    PaymentAutoRedirectionConfirm,
  },
  created() {
    this.initializeSelectableOptions();
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  data() {
    return {
      selectList: [],
      sending: false,
      payloadData: {
        name: {
          text: this.$t('sender.firstname'),
          selected: false,
        },
        email: {
          text: this.$t('sender.email'),
          selected: false,
        },
        phone: {
          text: this.$t('sender.phone'),
          selected: false,
        },
        comment: {
          text: this.$t('sender.comment'),
          selected: false,
        },
        message: {
          text: this.$t('sender.message'),
          selected: false,
        },
        address: {
          text: this.$t('sender.address'),
          selected: false,
        },
      },
      mouseEvent: null,
      isShownSelectableOptions: false,
    };
  },
  computed: {
    hasSelectedItem() {
      return this.selectList.some(item => item.selected);
    },
    selectedItem() {
      return this.selectList
        .find(item => item.selected)?.key || 'comment';
    },
    isPaymentAutoRedirection() {
      const { params } = this.PROJECT_INFO;

      return params.payment_auto_redirection;
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters(['isDesktop']),
  },
  methods: {
    initializeSelectableOptions() {
      if (this.PROJECT_INFO.senderFields) {
        this.payloadData = Object.assign({}, this.PROJECT_INFO.senderFields);
      }

      this.selectList = Object.keys(this.payloadData)
        .filter(itemKey => SELECTABLE_ITEMS.includes(itemKey))
        .map(itemKey => {
          return {
            ...this.payloadData[itemKey],
            key: itemKey,
            description: this.$t(this.payloadData[itemKey].text),
            active: this.payloadData[itemKey].selected,
          };
        });
    },
    handleScroll() {
      if (!this.isDesktop) {
        return;
      }

      if (this.$validate.isNull(this.mouseEvent)) {
        return;
      }

      this.$refs.capustaPopup.setPosition(this.mouseEvent);
    },
    handleClickToSelectableOption($event) {
      if (this.isDesktop) {
        this.mouseEvent = $event;
        this.$refs.capustaPopup.openMenu();
        this.$refs.capustaPopup.setPosition($event);
        return;
      }

      this.isShownSelectableOptions = true;
    },
    handleSelectOption(option) {
      this.$refs.capustaPopup?.closeMenu();
      this.isShownSelectableOptions = false;

      if (option.selected) {
        return;
      }

      this.selectList.forEach(item => {
        this.payloadData[item.key].selected = false;
      });

      this.handleChangeSenderField(option.key);
    },
    handleChangeSenderField(name) {
      if (this.sending) {
        return;
      }

      this.sending = true;
      this.payloadData[name].selected = !this.payloadData[name].selected;

      const paymentAutoRedirection = this.payloadData.email.selected
        ? this.PROJECT_INFO.params.payment_auto_redirection
        : this.payloadData.email.selected;

      const payloadData = {
        ...this.PROJECT_INFO,
        params: {
          ...this.PROJECT_INFO.params,
          payment_auto_redirection: paymentAutoRedirection,
        },
        senderFields: this.payloadData,
      };

      HTTPService.editProject(payloadData, payloadData.projectId)
        .then(() => {
          this.SET_PROJECT_INFO(payloadData);

          this.$alert({
            type: 'cabbage',
            text: 'settings.updated',
          });

          this.initializeSelectableOptions();
        })
        .catch(() => {
          this.$alert({
            type: 'warning',
            text: 'problems.on.server.try.later',
          });

          if (name) {
            this.payloadData[name].selected = false;
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },
    async handlePreview() {
      try {
        const bill = new BillForm;
        bill.currency = this.PROJECT_INFO.currency;

        const { data } = await HTTPService.createBill({
          ...bill.getData(),
          multiBill: false,
          preview: true,
        });

        window.location.assign(data.url);
      } catch (error) {
        logrocket.logger(error);
      }
    },
    handleChangeGradeSettings(data) {
      const payloadData = {
        ...this.PROJECT_INFO,
        senderFields: {
          ...this.payloadData,
          rating: data,
        },
      };

      HTTPService.editProject(payloadData, payloadData.projectId)
        .then(() => {
          this.SET_PROJECT_INFO(payloadData);

          this.payloadData.rating.question = data.question;
          this.payloadData.rating.type = data.type;

          this.$alert({
            type: 'cabbage',
            text: 'settings.updated',
          });
        })
        .catch(() => {
          this.$alert({
            type: 'warning',
            text: 'problems.on.server.try.later',
          });
        });
    },
    handleChangeEmailSender() {
      const isCheckedEmail = this.payloadData.email.selected;

      if (isCheckedEmail && this.isPaymentAutoRedirection) {
        EVENT_BUS.$emit('EVENT_CONFIRM_TO_PAYMENT_AUTO_REDIRECTION');
      } else {
        this.handleChangeSenderField('email');
      }
    },
    ...mapMutations(['SET_PROJECT_INFO']),
  },
};
</script>

<style lang="sass" scoped>
  .popup-selectable-container
    display: inline-flex

  .selectable-container
    display: flex
    align-items: center

  .selectable-title
    margin-right: 5px

  .selectable-icon
    width: 15px
    margin-right: 10px

    +respond-to('cramped')
      width: 20px

  .submit-button-container
    max-width: 300px
    margin: 30px auto 0
    padding: 0 20px

    +respond-to('medium')
      margin-top: 40px
</style>
